import React from 'react'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons/faInfoCircle'

import { Field, Label, Error, Required, Hint, HintBox } from './styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import SrOnly from '../SrOnly'
import Tooltip, { TooltipTrigger } from '../Tooltip'
import { Trans } from 'react-i18next'
import { AlignedPlacement, Side } from '@floating-ui/react'
import { AllColors } from '../../types/Colors'
import DynamicTooltipTypography from '../DynamicTooltipTypography'

export interface HintProp {
  title?: React.ReactNode
  position?: 'top' | 'bottom' | 'afterLabel'
  trans?: {
    i18nKey: any
    ns: any
  }
  gutterBottom?: number | boolean
  className?: string
}

export interface HintBoxProp {
  icon?: IconDefinition
  title: string
  color?: AllColors
}

export interface LabelIcon {
  icon: IconDefinition
  description?: string
  color?: string
}

export interface FormControlProps {
  label?: string | JSX.Element
  required?: boolean
  inputId?: string
  labelId?: string
  children: string | JSX.Element | JSX.Element[]
  fullWidth?: boolean
  className?: string
  gutterBottom?: boolean | string
  error?: string | boolean
  hint?: HintProp
  hiddenLabel?: boolean
  disabled?: boolean
  hintBox?: HintBoxProp
  labelIcon?: LabelIcon
  tooltip?: string
  tooltipPlacement?: Side | AlignedPlacement
}

const FormControl: React.FC<FormControlProps> = ({
  children,
  inputId,
  label,
  error,
  required,
  labelId,
  fullWidth,
  gutterBottom,
  hint,
  className,
  hiddenLabel,
  disabled,
  hintBox,
  labelIcon,
  tooltip,
  tooltipPlacement,
}) => {
  if (!!hint?.title && !hint.position) hint.position = 'bottom'
  return (
    <Field fullWidth={fullWidth} gutterBottom={gutterBottom} className={className}>
      {!!label && (
        <Label htmlFor={inputId} id={labelId} error={!!error} hiddenLabel={hiddenLabel} disabled={disabled}>
          <DynamicTooltipTypography
            as="span"
            className="typography"
            tooltip={{
              placement: 'top-start',
              offsetValue: 8,
              description: label,
            }}
          >
            <span>
              {label}
              {labelIcon && (
                <>
                  <FontAwesomeIcon
                    icon={labelIcon.icon}
                    color={labelIcon.color}
                    aria-hidden="true"
                    className="label-icon"
                  />
                  <SrOnly>({labelIcon.description})</SrOnly>
                </>
              )}
              {tooltip && (
                <Tooltip hasArrow offsetValue={12} title={tooltip} maxWidth={265} placement={tooltipPlacement}>
                  <TooltipTrigger asChild>
                    <FontAwesomeIcon icon={faInfoCircle} aria-hidden="true" className="label-icon" />
                  </TooltipTrigger>
                </Tooltip>
              )}
              {required && !labelIcon && <Required />}
            </span>
          </DynamicTooltipTypography>
          {hint?.position === 'afterLabel' && (
            <Hint gutterBottom={hint.gutterBottom} className={hint.className}>
              {!!hint.trans ? <Trans i18nKey={hint.trans.i18nKey} ns={hint.trans.ns} /> : hint.title}
            </Hint>
          )}
        </Label>
      )}
      {hint?.position === 'top' && (
        <Hint gutterBottom={hint.gutterBottom} className={hint.className}>
          {!!hint.trans ? <Trans i18nKey={hint.trans.i18nKey} ns={hint.trans.ns} /> : hint.title}
        </Hint>
      )}
      {children}
      {typeof error === 'string' && <Error>{error}</Error>}
      {hint?.position === 'bottom' && (
        <Hint gutterBottom={hint.gutterBottom} className={hint.className}>
          {!!hint.trans ? <Trans i18nKey={hint.trans.i18nKey} ns={hint.trans.ns} /> : hint.title}
        </Hint>
      )}
      {!!hintBox && (
        <HintBox
          title={hintBox.title}
          icon={hintBox?.icon === undefined ? faInfoCircle : hintBox?.icon}
          color={hintBox.color}
        />
      )}
    </Field>
  )
}
export default FormControl
